import { FC } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { useFormat } from '../../../../hooks/index';
import { SummaryItem } from '../../summary-item';

type VestingProgressBarProps = {
  grantDate?: Date | string;
  grantId?: string;
  totalShares: number;
  vestedShares: number;
};

/**
 * Vesting progress on a phone, where the chart has no room and the grant's dates are
 * already listed in the attributes above. Also the shape the vesting emails render.
 */
export const VestingProgressBar: FC<VestingProgressBarProps> = ({
  grantDate,
  grantId,
  totalShares,
  vestedShares,
}) => {
  const { format } = useFormat();
  const filledPercent = totalShares > 0 ? Math.min((vestedShares / totalShares) * 100, 100) : 0;
  const isPartialFill = filledPercent > 0 && filledPercent < 100;

  return (
    <div className="flex w-full flex-col overflow-hidden rounded-md bg-white shadow-sm">
      <div className="divide-y divide-gray-100">
        {grantId && <SummaryItem title={<AppFormattedMessage id="GRANT_ID" />} value={grantId} />}
        {grantDate && (
          <SummaryItem
            title={<AppFormattedMessage id="GRANT_DATE" />}
            value={format(grantDate, 'dd/MM/yyyy')}
          />
        )}
      </div>

      <div className="flex flex-col py-2">
        <div className="flex w-full flex-col gap-4 py-3 pl-3 pr-5">
          <div className="flex flex-col gap-[5px]">
            <span className="text-xs font-[450] leading-4 text-gray-400">
              <AppFormattedMessage id="VESTING_PROGRESS" />
            </span>
            <span className="text-sm font-[450] leading-5 text-gray-700">
              <AppFormattedMessage
                id="VESTING_PROGRESS_GRANTED"
                values={{ count: totalShares.toLocaleString('en-US') }}
              />
            </span>
          </div>

          <div
            aria-label={`${vestedShares.toLocaleString('en-US')} of ${totalShares.toLocaleString('en-US')} vested`}
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={Number(filledPercent.toFixed(1))}
            className="h-3 w-full overflow-hidden rounded-3xl bg-gray-200"
            role="progressbar"
          >
            <div
              className="h-full bg-gradient-to-r from-forest-500 to-forest-300"
              style={{
                clipPath: isPartialFill
                  ? 'polygon(0 0, calc(100% - 12px) 0, 100% 100%, 0 100%)'
                  : undefined,
                width: `${filledPercent}%`,
              }}
            />
          </div>

          <span className="flex items-center gap-2 text-xs font-medium leading-[18px] text-gray-700">
            <span className="size-2 shrink-0 rounded-full bg-forest-500" />
            <AppFormattedMessage
              id="VESTING_PROGRESS_VESTED"
              values={{ count: vestedShares.toLocaleString('en-US') }}
            />
          </span>
        </div>
      </div>
    </div>
  );
};
